// Lógica compartilhada do funil comercial (extraída do padrão usado no KPIDashboard).
// O funil é derivado do campo `status` da negociação; quem assina e pausa continua
// contando como "termo_assinado" alcançado.

export interface FunnelRow {
    status: string;
    data_assinatura_termo?: string | null;
}

export const STATUS_ORDER = ['em_andamento', 'simulacao_realizada', 'termo_enviado', 'termo_assinado', 'termo_analise', 'termo_aprovado', 'ganho'];

export const statusAtOrAfter = (status: string, target: string): boolean => {
    const i = STATUS_ORDER.indexOf(status);
    const t = STATUS_ORDER.indexOf(target);
    return i !== -1 && t !== -1 && i >= t;
};

export const isEffectivelyAtOrAfter = (r: FunnelRow, target: string): boolean => {
    if (statusAtOrAfter(r.status, target)) return true;
    if (r.status === 'em_pausa' && r.data_assinatura_termo) return statusAtOrAfter('termo_assinado', target);
    return false;
};

/** Etapas do funil macro (ponta a ponta). target=null conta todos os leads. */
export const FUNNEL_STAGES: Array<{ key: string; label: string; color: string; target: string | null }> = [
    { key: 'prospeccao', label: 'Prospecção', color: '#3b82f6', target: null },
    { key: 'simulacao', label: 'Simulação', color: '#a855f7', target: 'simulacao_realizada' },
    { key: 'termo_enviado', label: 'Termo Enviado', color: '#eab308', target: 'termo_enviado' },
    { key: 'termo_assinado', label: 'Termo Assinado', color: '#14b8a6', target: 'termo_assinado' },
    { key: 'termo_aprovado', label: 'Termo Aprovado', color: '#22c55e', target: 'termo_aprovado' },
];

/** Rótulo de exibição dos 10 valores de status — cobre o funil completo, inclusive os
 *  desvios (perdido/em_pausa/termo_reprovado) que FUNNEL_STAGES não rotula. Usado pelo
 *  filtro "status do funil" do Analytics Comercial. */
export const STATUS_LABELS: Record<string, string> = {
    em_andamento: 'Em andamento',
    simulacao_realizada: 'Simulação realizada',
    termo_enviado: 'Termo enviado',
    termo_assinado: 'Termo assinado',
    termo_analise: 'Termo em análise',
    termo_aprovado: 'Termo aprovado',
    termo_reprovado: 'Termo reprovado',
    ganho: 'Ganho',
    perdido: 'Perdido',
    em_pausa: 'Em pausa',
};

export interface FunnelCounts {
    counts: Record<string, number>;
    /** Conversão etapa→etapa (i → i+1) em %. */
    stepRates: Array<{ from: string; to: string; rate: number }>;
}

export const computeFunnel = (rows: FunnelRow[]): FunnelCounts => {
    const counts: Record<string, number> = {};
    FUNNEL_STAGES.forEach(s => {
        counts[s.key] = s.target === null ? rows.length : rows.filter(r => isEffectivelyAtOrAfter(r, s.target!)).length;
    });
    const stepRates: FunnelCounts['stepRates'] = [];
    for (let i = 0; i < FUNNEL_STAGES.length - 1; i++) {
        const from = FUNNEL_STAGES[i];
        const to = FUNNEL_STAGES[i + 1];
        const fc = counts[from.key];
        const tc = counts[to.key];
        stepRates.push({ from: from.label, to: to.label, rate: fc > 0 ? (tc / fc) * 100 : 0 });
    }
    return { counts, stepRates };
};

export const fmtNum = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
export const fmtPct = (v: number) => `${v.toFixed(1)}%`;
export const fmtKwh = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh`;
